// The price helpers live in @adiox/ui, shared with the manager's Market Watch.
export { changePercent, splitPrice, spreadPoints, type PriceParts } from '@adiox/ui';
/** Pure logic of the Market Watch: which symbols are listed, in what order, and how prices are split for display. */

export const PREFERRED_DEFAULTS = [
  'EURUSD',
  'GBPUSD',
  'USDJPY',
  'USDCHF',
  'AUDUSD',
  'USDCAD',
  'XAUUSD',
  'US30',
  'US500',
  'BTCUSD',
  'ETHUSD',
];

export interface SymbolLike {
  name: string;
  category: string;
  description: string;
  digits: number;
}

/** The list shown until the user changes it: familiar symbols first, or simply the first few the group offers. */
export function defaultWatchList(available: SymbolLike[]): string[] {
  const names = new Set(available.map((s) => s.name));
  const preferred = PREFERRED_DEFAULTS.filter((n) => names.has(n));
  return preferred.length > 0 ? preferred : available.slice(0, 10).map((s) => s.name);
}

/** The saved list without symbols the group no longer offers; null means "nothing saved yet". */
export function effectiveWatchList(saved: string[] | null, available: SymbolLike[]): string[] {
  if (saved === null) return defaultWatchList(available);
  const names = new Set(available.map((s) => s.name));
  const seen = new Set<string>();
  return saved.filter((n) => names.has(n) && !seen.has(n) && seen.add(n));
}

export const addSymbol = (list: string[], name: string): string[] =>
  list.includes(name) ? list : [...list, name];

export const removeSymbol = (list: string[], name: string): string[] => list.filter((n) => n !== name);

/** Every symbol the group offers: the list as it is, then whatever was hidden, in the server's order. */
export function showAll(list: string[], available: SymbolLike[]): string[] {
  const shown = new Set(list);
  return [...list, ...available.map((s) => s.name).filter((n) => !shown.has(n))];
}

/** Moves `name` to `toIndex` (clamped); unknown names change nothing. */
export function moveSymbol(list: string[], name: string, toIndex: number): string[] {
  const from = list.indexOf(name);
  if (from === -1) return list;
  const target = Math.max(0, Math.min(list.length - 1, toIndex));
  if (target === from) return list;
  const next = [...list];
  next.splice(from, 1);
  next.splice(target, 0, name);
  return next;
}

/** Symbols not in the list yet that match the search text (name or description), grouped by category, sorted. */
export function addableGroups(
  available: SymbolLike[],
  list: string[],
  search: string,
): { category: string; symbols: SymbolLike[] }[] {
  const text = search.trim().toLowerCase();
  const inList = new Set(list);
  const groups = new Map<string, SymbolLike[]>();
  for (const s of available) {
    if (inList.has(s.name)) continue;
    if (text && !s.name.toLowerCase().includes(text) && !s.description.toLowerCase().includes(text)) continue;
    const bucket = groups.get(s.category) ?? [];
    bucket.push(s);
    groups.set(s.category, bucket);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, symbols]) => ({
      category,
      symbols: symbols.sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

export const matchesFilter = (s: SymbolLike | undefined, name: string, filter: string): boolean => {
  const text = filter.trim().toLowerCase();
  return !text || name.toLowerCase().includes(text) || (s?.description ?? '').toLowerCase().includes(text);
};

export const formatPrice = (value: number, digits: number): string => value.toFixed(digits);
